// src/components/ContactPage.tsx
// Contact pagina met formulier
// Modern Glassmorphism Design - Fully Responsive
// Gebruikt eigen backend API voor email verzending

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// ============================================
// CONFIGURATIE
// ============================================

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

// ============================================
// TYPES
// ============================================

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

// ============================================
// CONTACT PAGE COMPONENT
// ============================================

const ContactPage: React.FC = () => {
  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: 'Algemene vraag',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Subject options
  const subjectOptions = [
    'Algemene vraag',
    'Vraag over mijn account',
    'Technisch probleem',
    'Feedback of suggestie',
    'Samenwerking of partnership',
    'Anders',
  ];

  // Validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Vul je naam in';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Vul je e-mailadres in';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Vul een geldig e-mailadres in';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Vul je bericht in';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Je bericht moet minimaal 10 karakters bevatten';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          subject: formData.subject,
          message: formData.message.trim(),
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: 'Algemene vraag',
          message: '',
        });
      } else {
        setSubmitStatus('error');
        setErrorMessage(data.error || 'Er ging iets mis bij het versturen.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      setErrorMessage('Kon geen verbinding maken met de server. Probeer het later opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact | WFT Study Buddy</title>
        <meta name="description" content="Neem contact op met WFT Study Buddy. Heb je vragen over je WFT examenvoorbereiding? Wij helpen je graag verder." />
        <link rel="canonical" href="https://wft-study-buddy.nl/contact" />
        <link rel="alternate" hrefLang="nl-NL" href="https://wft-study-buddy.nl/contact" />
        <link rel="alternate" hrefLang="x-default" href="https://wft-study-buddy.nl/contact" />
        <meta property="og:title" content="Contact | WFT Study Buddy" />
        <meta property="og:description" content="Neem contact op met WFT Study Buddy. Heb je vragen over je WFT examenvoorbereiding?" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://wft-study-buddy.nl/contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact - WFT Study Buddy",
            "url": "https://wft-study-buddy.nl/contact",
            "mainEntity": {
              "@type": "Organization",
              "name": "WFT Study Buddy",
              "url": "https://wft-study-buddy.nl",
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "Customer Support",
                "email": "info@wft-study-buddy.nl",
                "availableLanguage": "Dutch"
              }
            }
          })}
        </script>
      </Helmet>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-lg border-b border-gray-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <span className="text-2xl">📚</span>
              <span className="font-bold text-xl bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                WFT Study Buddy
              </span>
            </Link>
            
            <div className="flex items-center space-x-4">
              <Link 
                to="/" 
                className="text-gray-600 hover:text-gray-900 transition font-medium"
              >
                Home
              </Link>
              <Link 
                to="/wft-examens" 
                className="text-gray-600 hover:text-gray-900 transition font-medium hidden sm:block"
              >
                Examens
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-4">
              📬 Contact
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4">
              Neem contact op
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Heb je een vraag, feedback of suggestie? We horen graag van je!
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Contact Info Cards */}
            <div className="lg:col-span-1 space-y-4">
              {/* Response Time */}
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-2xl mb-4">
                  ⏱️
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Reactietijd</h3>
                <p className="text-gray-600 text-sm">
                  We proberen binnen 24-48 uur te reageren op werkdagen.
                </p>
              </div>

              {/* FAQ Link */}
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center text-2xl mb-4">
                  ❓
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Veelgestelde vragen</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Misschien staat je antwoord al in onze FAQ.
                </p>
                <Link 
                  to="/#faq" 
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm inline-flex items-center"
                >
                  Bekijk FAQ
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/50 shadow-xl">
                
                {/* Success Message */}
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-xl mr-4 flex-shrink-0">
                        ✅
                      </div>
                      <div>
                        <h4 className="font-semibold text-emerald-800 mb-1">
                          Bericht verzonden!
                        </h4>
                        <p className="text-emerald-700 text-sm">
                          Bedankt voor je bericht. We nemen zo snel mogelijk contact met je op.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center text-xl mr-4 flex-shrink-0">
                        ❌
                      </div>
                      <div>
                        <h4 className="font-semibold text-red-800 mb-1">
                          Er ging iets mis
                        </h4>
                        <p className="text-red-700 text-sm">
                          {errorMessage || 'Het versturen is mislukt. Probeer het later opnieuw.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name & Email Row */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {/* Name */}
                    <div>
                      <label 
                        htmlFor="name" 
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Naam *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`
                          w-full px-4 py-3 rounded-xl border bg-white/50
                          focus:ring-2 focus:ring-blue-500 focus:border-transparent
                          transition-all duration-200
                          ${errors.name 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'border-gray-200 hover:border-gray-300'
                          }
                        `}
                        placeholder="Je naam"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label 
                        htmlFor="email" 
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        E-mailadres *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`
                          w-full px-4 py-3 rounded-xl border bg-white/50
                          focus:ring-2 focus:ring-blue-500 focus:border-transparent
                          transition-all duration-200
                          ${errors.email 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'border-gray-200 hover:border-gray-300'
                          }
                        `}
                        placeholder="je@email.nl"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                      )}
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label 
                      htmlFor="subject" 
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Onderwerp
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="
                        w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50
                        focus:ring-2 focus:ring-blue-500 focus:border-transparent
                        transition-all duration-200 hover:border-gray-300
                        appearance-none cursor-pointer
                      "
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 1rem center',
                        backgroundSize: '1.5rem',
                      }}
                    >
                      {subjectOptions.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label 
                      htmlFor="message" 
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Bericht *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className={`
                        w-full px-4 py-3 rounded-xl border bg-white/50
                        focus:ring-2 focus:ring-blue-500 focus:border-transparent
                        transition-all duration-200 resize-none
                        ${errors.message 
                          ? 'border-red-300 focus:ring-red-500' 
                          : 'border-gray-200 hover:border-gray-300'
                        }
                      `}
                      placeholder="Schrijf hier je vraag of bericht..."
                    />
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`
                      w-full py-4 px-6 rounded-xl font-semibold text-lg
                      transition-all duration-300 transform
                      ${isSubmitting
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-xl hover:scale-[1.02]'
                      }
                    `}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Versturen...
                      </span>
                    ) : (
                      'Verstuur bericht →'
                    )}
                  </button>

                  {/* Privacy Note */}
                  <p className="text-xs text-gray-500 text-center">
                    Door dit formulier te versturen ga je akkoord met onze{' '}
                    <Link to="/privacy" className="text-blue-600 hover:underline">
                      privacyverklaring
                    </Link>
                    .
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">
            © <span suppressHydrationWarning>{new Date().getFullYear()}</span>WFT Study Buddy. Alle rechten voorbehouden.
          </p>
        </div>
      </footer>
    </div>
    </>
  );
};

export default ContactPage;